import { type RefObject, useRef, useState } from "react";

import { useResizeObserver } from "./useResizeObserver";

type UseToggleDragProps = {
  checked: boolean;
  onChange: ((checked: boolean) => void) | undefined;
};

export const useToggleDrag = ({ checked, onChange }: UseToggleDragProps) => {
  const trackRef: RefObject<HTMLDivElement | null> = useRef(null);
  const thumbRef = useRef<HTMLSpanElement>(null);
  const [dragX, setDragX] = useState<number | undefined>(undefined);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStopped, setDragStopped] = useState(0);

  const { height: thumbHeight = 0 } = useResizeObserver({ ref: thumbRef, round: true });
  const { height: switchHeight = 0 } = useResizeObserver({ ref: trackRef, round: true });

  const switchThumbHeightDelta = (switchHeight || 0) - thumbHeight;
  const maxDragRange = thumbHeight + switchThumbHeightDelta;
  const minDragRange = switchThumbHeightDelta / 2;
  const halfMaxDragRange = maxDragRange / 2;
  const isDraggedHalfway =
    typeof dragX === "number" && (checked ? dragX < halfMaxDragRange : dragX > halfMaxDragRange);

  const onDrag = ({ x }: { x: number }) => {
    setDragX(x);
    const dragDelta = Math.abs((checked ? maxDragRange : minDragRange) - x);

    // Add a threshold here for then accidentally dragging when clicking/touching
    dragDelta > 2 && setIsDragging(true);
  };

  const onStop = () => {
    setIsDragging(false);
    isDragging && setDragStopped(Date.now());
    if (isDraggedHalfway) {
      onChange?.(!checked);
    }
  };

  const onChangeWithDrag = () => {
    if (Date.now() - dragStopped > 50) {
      onChange?.(!checked);
    }
    setDragX(undefined);
  };

  return {
    trackRef,
    thumbRef,
    isDragging,
    bounds: { left: minDragRange, right: maxDragRange },
    position: checked ? maxDragRange : minDragRange,
    onDrag,
    onStop,
    onChangeWithDrag,
  };
};
